import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles,
  TrendingUp,
  Users,
  Wand2,
  Utensils,
  Rocket,
  ShieldCheck,
  Mail,
  Zap,
  Trophy,
  KeyRound,
  Loader2,
  X,
} from "lucide-react";
import Footer from "../components/Footer";
import useSeo from "../lib/useSeo";

/**
 * /investors — Plate Buddies investor deck, unified into the parent-facing
 * brand system (cream / navy / coral). All data pulled verbatim from the
 * published deck at makeahappyplate.com with inline source citations.
 *
 * Access gate mirrors the real deck's flow:
 *   • "Request Access"  → logs lead to backend (`POST /api/investors/request`)
 *   • "I have a code"   → validates code (`POST /api/investors/verify`), stashes
 *                         a session marker so the gate stays cleared on reload
 *
 * SEO: robots noindex — investor material should not compete for parent
 * search intent.
 */

const GATE_KEY = "pb_investors_access_token";
const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// ---- VERIFIED DATA (from the published deck on makeahappyplate.com) ----

const TRACTION = [
  {
    icon: Zap,
    label: "Live platform. Real families. Real orders. $5 per custom Buddy.",
    value: "Live today",
    detail: "platebuddies.com is in-market",
  },
  {
    icon: Users,
    label: "The exact window when lifelong food habits form.",
    value: "Ages 2–7",
    detail: "Core user window",
  },
  {
    icon: TrendingUp,
    label: "Nearest comp (collectible characters + audio play) — 2025 revenue.",
    value: "~$681M",
    detail: "Tonies SE 2025",
  },
];

const PROBLEM = [
  { stat: "~50%", label: "of children aged 2–5 go through a selective-eating phase", source: "American Academy of Pediatrics" },
  { stat: "$2B+", label: "spent annually by parents on feeding solutions", source: "US feeding & nutrition category" },
  { stat: "0", label: "existing products use play-based behavioral reinforcement at the plate", source: "Market gap" },
];

const MARKET = [
  { number: "$931M", label: "US children's tableware market", source: "3rd-party market reports" },
  { number: "14.9M", label: "US children aged 2–5", source: "US Census Bureau" },
  { number: "~$681M", label: "Tonies 2025 revenue (USD equiv.)", source: "Tonies SE reported revenue" },
  { number: "~50%", label: "children 2–5 who go through selective eating", source: "AAP" },
];

const HOW_IT_WORKS = [
  { step: "01", title: "Place a Buddy", body: "Slide Captain Sammy or any of the 6 included buddies under the clear plate.", icon: Sparkles },
  { step: "02", title: "Explore food", body: "LED ring glows, sounds celebrate every interaction. Press \"I explored it\" for each food tried.", icon: Zap },
  { step: "03", title: "All done", body: "Hold the button for the big celebration. Lights flash green, Sammy cheers.", icon: Trophy },
];

const PRODUCT_FEATURES = [
  "LED ring celebrates every food explored — not just eaten",
  "6 collectible Buddy characters included, each with unique personality",
  "Custom buddies via PlateBuddies.com",
  "Fully washable — dishwasher-safe plate",
];

// =====================================================================
// ACCESS GATE
// =====================================================================

function AccessGate({ onUnlock }) {
  const [tab, setTab] = useState("code"); // "code" | "request"
  const [code, setCode] = useState("");
  const [reqForm, setReqForm] = useState({ name: "", email: "", firm: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const verifyCode = async (e) => {
    e.preventDefault();
    setError("");
    if (!code.trim()) {
      setError("Enter the access code to continue.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/investors/verify`, { code: code.trim() });
      if (data?.token) {
        onUnlock(data.token);
      } else {
        setError("Something went wrong. Try again.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Invalid code.";
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!reqForm.name.trim() || !reqForm.email.trim()) {
      setError("Name and email are required.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/investors/request`, reqForm);
      setSuccess("Request received. We'll be in touch with access shortly.");
      setReqForm({ name: "", email: "", firm: "", note: "" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't submit — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="font-body min-h-screen bg-pb-cream text-pb-ink flex items-center justify-center px-5 sm:px-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" data-testid="gate-logo" className="inline-block">
            <img
              src="/brand/logo-wordmark.png"
              alt="Plate Buddies"
              className="h-14 w-auto mx-auto"
            />
          </Link>
        </div>

        <div className="bg-white rounded-[2rem] shadow-[0_30px_80px_rgba(28,22,40,0.14)] border border-pb-cream overflow-hidden">
          <div className="p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-5">
              <span className="h-9 w-9 rounded-2xl bg-pb-coral/10 inline-flex items-center justify-center">
                <ShieldCheck className="h-4 w-4 text-pb-coral" />
              </span>
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-pb-coral">
                  Confidential
                </p>
                <h1 className="font-display font-black text-xl text-pb-ink leading-tight">
                  Investor overview
                </h1>
              </div>
            </div>

            {/* Tab switch */}
            <div className="grid grid-cols-2 gap-1 bg-pb-cream rounded-full p-1 mb-6">
              <button
                data-testid="gate-tab-code"
                type="button"
                onClick={() => { setTab("code"); setError(""); setSuccess(""); }}
                className={`py-2 rounded-full text-xs font-extrabold uppercase tracking-widest transition ${
                  tab === "code" ? "bg-white text-pb-coral shadow-sm" : "text-pb-soft hover:text-pb-ink"
                }`}
              >
                I have a code
              </button>
              <button
                data-testid="gate-tab-request"
                type="button"
                onClick={() => { setTab("request"); setError(""); setSuccess(""); }}
                className={`py-2 rounded-full text-xs font-extrabold uppercase tracking-widest transition ${
                  tab === "request" ? "bg-white text-pb-coral shadow-sm" : "text-pb-soft hover:text-pb-ink"
                }`}
              >
                Request access
              </button>
            </div>

            {tab === "code" ? (
              <form onSubmit={verifyCode} className="space-y-3">
                <label className="block">
                  <span className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">
                    Access code
                  </span>
                  <div className="mt-1 relative">
                    <KeyRound className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-pb-soft" />
                    <input
                      data-testid="gate-code-input"
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder="Enter your code"
                      autoFocus
                      className="w-full rounded-xl border-2 border-pb-cream bg-white pl-10 pr-3 py-3 font-mono text-sm text-pb-ink placeholder-pb-soft/50 focus:border-pb-coral focus:outline-none"
                    />
                  </div>
                </label>
                <button
                  data-testid="gate-code-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-full bg-pb-coral text-white font-extrabold px-5 py-3 inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  View the deck
                </button>
              </form>
            ) : (
              <form onSubmit={submitRequest} className="space-y-3">
                <label className="block">
                  <span className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">Name</span>
                  <input
                    data-testid="gate-request-name"
                    type="text"
                    value={reqForm.name}
                    onChange={(e) => setReqForm({ ...reqForm, name: e.target.value })}
                    className="mt-1 w-full rounded-xl border-2 border-pb-cream bg-white px-3 py-2.5 text-sm focus:border-pb-coral focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">Email</span>
                  <input
                    data-testid="gate-request-email"
                    type="email"
                    value={reqForm.email}
                    onChange={(e) => setReqForm({ ...reqForm, email: e.target.value })}
                    className="mt-1 w-full rounded-xl border-2 border-pb-cream bg-white px-3 py-2.5 text-sm focus:border-pb-coral focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">Firm (optional)</span>
                  <input
                    data-testid="gate-request-firm"
                    type="text"
                    value={reqForm.firm}
                    onChange={(e) => setReqForm({ ...reqForm, firm: e.target.value })}
                    className="mt-1 w-full rounded-xl border-2 border-pb-cream bg-white px-3 py-2.5 text-sm focus:border-pb-coral focus:outline-none"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">Note (optional)</span>
                  <textarea
                    data-testid="gate-request-note"
                    rows={2}
                    value={reqForm.note}
                    onChange={(e) => setReqForm({ ...reqForm, note: e.target.value })}
                    placeholder="A sentence about why you're interested."
                    className="mt-1 w-full rounded-xl border-2 border-pb-cream bg-white px-3 py-2.5 text-sm focus:border-pb-coral focus:outline-none resize-none"
                  />
                </label>
                <button
                  data-testid="gate-request-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-full bg-pb-coral text-white font-extrabold px-5 py-3 inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                  Request access
                </button>
              </form>
            )}

            {error && (
              <p data-testid="gate-error" className="mt-4 text-xs text-pb-coral font-bold flex items-center gap-1.5">
                <X className="h-3 w-3" /> {error}
              </p>
            )}
            {success && (
              <p data-testid="gate-success" className="mt-4 text-xs text-pb-green font-bold">
                {success}
              </p>
            )}
          </div>
          <div className="bg-pb-cream/60 px-6 sm:px-8 py-4 text-[11px] text-pb-soft/80 text-center">
            Not an investor?{" "}
            <Link to="/" className="font-extrabold text-pb-coral hover:underline">
              Return to platebuddies.com →
            </Link>
          </div>
        </div>

        <p className="mt-6 text-center text-[11px] text-pb-soft/70">
          Confidential. Plate Buddies, 2026.
        </p>
      </div>
    </div>
  );
}

// =====================================================================
// INVESTOR DECK (unlocked)
// =====================================================================

function DeckContent({ onLock }) {
  return (
    <div className="font-body min-h-screen bg-pb-cream text-pb-ink">
      {/* Minimal header */}
      <header className="max-w-6xl mx-auto px-5 sm:px-8 pt-6 flex items-center justify-between">
        <Link to="/" data-testid="investors-logo" className="inline-flex items-center">
          <img
            src="/brand/logo-wordmark.png"
            alt="Plate Buddies"
            className="h-11 w-auto"
          />
        </Link>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 rounded-full bg-pb-ink text-pb-cream px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-widest">
            <ShieldCheck className="h-3 w-3" /> Confidential
          </span>
          <button
            data-testid="btn-lock-deck"
            onClick={onLock}
            title="Lock the deck on this device"
            className="h-8 w-8 rounded-full border-2 border-pb-soft/20 text-pb-soft hover:border-pb-coral hover:text-pb-coral transition inline-flex items-center justify-center"
          >
            <KeyRound className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-24">
        {/* Hero */}
        <section data-testid="investors-hero" className="text-center">
          <p className="text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            Interactive mealtime brand · Ages 2–7
          </p>
          <h1 className="mt-4 font-display font-black text-pb-ink text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl mx-auto">
            Make exploring food <span className="text-pb-coral">feel like play.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-pb-soft max-w-2xl mx-auto leading-relaxed">
            An interactive plate system that uses lights, sounds, and collectible Buddy characters to turn
            mealtime from a battle into a little victory — backed by the live custom-Buddy platform at{" "}
            <Link to="/" className="underline decoration-pb-coral/60 hover:text-pb-coral">
              platebuddies.com
            </Link>
            .
          </p>
        </section>

        {/* Traction */}
        <section data-testid="investors-traction" className="mt-16">
          <p className="text-center text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            Traction
          </p>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-center mt-2">
            The market is already responding.
          </h2>
          <p className="text-center text-sm text-pb-soft mt-3 max-w-xl mx-auto">
            PlateBuddies isn't starting from zero. The custom-Buddy platform is live and taking orders from
            parents today.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {TRACTION.map((t) => (
              <div key={t.label} className="bg-white rounded-3xl p-6 border border-pb-cream shadow-sm">
                <span className="h-10 w-10 rounded-2xl bg-pb-cream inline-flex items-center justify-center">
                  <t.icon className="h-4 w-4 text-pb-coral" />
                </span>
                <p className="mt-4 font-display font-black text-3xl leading-none">{t.value}</p>
                <p className="mt-1 text-[11px] font-extrabold uppercase tracking-widest text-pb-coral">
                  {t.detail}
                </p>
                <p className="mt-3 text-xs text-pb-soft leading-snug">{t.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Problem */}
        <section data-testid="investors-problem" className="mt-16">
          <p className="text-center text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            The problem
          </p>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-center mt-2 max-w-2xl mx-auto">
            Mealtime is the most stressful part of parenting a toddler.
          </h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {PROBLEM.map((p) => (
              <div key={p.label} className="bg-white rounded-3xl p-6 border border-pb-cream shadow-sm">
                <p className="font-display font-black text-3xl leading-none text-pb-coral">{p.stat}</p>
                <p className="mt-3 text-sm text-pb-ink leading-snug">{p.label}</p>
                <p className="mt-3 text-[11px] text-pb-soft/80 italic">Source: {p.source}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-sm text-pb-ink/80 italic max-w-xl mx-auto">
            "Every parent knows the feeling. PlateBuddies changes the dynamic."
          </p>
        </section>

        {/* Product — the Starter Kit (physical) */}
        <section data-testid="investors-product" className="mt-16">
          <p className="text-center text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            Introducing the PlateBuddies™ Starter Kit
          </p>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-center mt-2 max-w-2xl mx-auto">
            The plate that celebrates every try.
          </h2>
          <p className="text-center text-sm text-pb-soft mt-3 max-w-2xl mx-auto leading-relaxed">
            An interactive mealtime plate system that uses lights, sounds, and collectible Buddy characters
            to make food exploration feel like play — not a battle.
          </p>
          {/* Real Starter Kit box renders from the deck */}
          <div className="mt-8 rounded-[2.5rem] overflow-hidden bg-white p-4 sm:p-6 border border-pb-cream shadow-sm">
            <img
              src="/investors/starter-kit-boxes.png"
              alt="Captain Sammy Starter Kit — front and back box renders showing the plate, LED module, and in-the-box contents."
              className="w-full h-auto block rounded-2xl"
              loading="lazy"
              data-testid="investors-starter-kit-image"
            />
          </div>
          <div className="mt-6 bg-white rounded-3xl p-6 sm:p-8 border border-pb-cream shadow-sm">
            <ul className="grid sm:grid-cols-2 gap-3">
              {PRODUCT_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <span className="flex-shrink-0 h-6 w-6 rounded-full bg-pb-green/20 text-pb-green inline-flex items-center justify-center font-black text-xs">
                    ✓
                  </span>
                  <span className="text-sm text-pb-ink leading-snug">{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* How it works — now with the real Captain Sammy character */}
        <section data-testid="investors-how" className="mt-16">
          <p className="text-center text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            How it works
          </p>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-center mt-2">
            Three steps to a little victory.
          </h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            {HOW_IT_WORKS.map((h, i) => (
              <div key={h.step} className="bg-white rounded-3xl p-6 border border-pb-cream shadow-sm">
                <div className="flex items-center gap-3">
                  <span className="font-display font-black text-xl text-pb-soft/40">{h.step}</span>
                  {i === 0 ? (
                    <img
                      src="/investors/captain-sammy.png"
                      alt="Captain Sammy — the first Plate Buddy character included in the Starter Kit."
                      className="h-14 w-14 rounded-full object-cover border-2 border-pb-cream"
                      data-testid="investors-captain-sammy"
                    />
                  ) : (
                    <span className="h-9 w-9 rounded-2xl bg-pb-coral/10 inline-flex items-center justify-center">
                      <h.icon className="h-4 w-4 text-pb-coral" />
                    </span>
                  )}
                </div>
                <p className="mt-3 font-display font-black text-lg">{h.title}</p>
                <p className="mt-1 text-sm text-pb-soft leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Market */}
        <section data-testid="investors-market" className="mt-16">
          <p className="text-center text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            Market opportunity
          </p>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-center mt-2">
            A big market for small explorers.
          </h2>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {MARKET.map((m) => (
              <div key={m.label} className="bg-pb-ink text-white rounded-3xl p-6">
                <p className="font-display font-black text-3xl text-pb-sun leading-none">{m.number}</p>
                <p className="mt-3 text-xs text-white/90 leading-snug">{m.label}</p>
                <p className="mt-3 text-[10px] text-white/50 italic">{m.source}</p>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-[11px] text-pb-soft/70 max-w-2xl mx-auto">
            Sources: American Academy of Pediatrics (selective-eating prevalence), Tonies SE reported 2025 revenue,
            US Census Bureau population estimates, and third-party children's tableware market reports.
          </p>
        </section>

        {/* Live validation */}
        <section data-testid="investors-live-gallery" className="mt-16">
          <p className="text-center text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            Validation
          </p>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-center mt-2">
            Buddies families have already created.
          </h2>
          <p className="mt-3 text-center text-sm text-pb-soft max-w-xl mx-auto">
            Every character below was dreamed up by a real family on{" "}
            <Link to="/" className="underline decoration-pb-coral/60 hover:text-pb-coral">
              platebuddies.com
            </Link>
            . The custom-Buddy platform is the validation + funnel for the physical plate.
          </p>
          <div className="mt-8 rounded-[2.5rem] overflow-hidden shadow-[0_30px_80px_rgba(28,22,40,0.14)] border-[6px] border-white">
            <img
              src="/etsy/three-frame-hero.jpg"
              alt="Plate Buddies product story: digital preview on phone → printed page → under a real kid's plate at dinner."
              className="w-full h-auto block"
              loading="lazy"
            />
          </div>
          <p className="mt-4 text-center text-xs text-pb-soft italic">
            Design. Print. Play. In under a minute.
          </p>
        </section>

        {/* Why now */}
        <section data-testid="investors-why-now" className="mt-16">
          <p className="text-center text-xs font-extrabold uppercase tracking-widest text-pb-coral">
            Why now
          </p>
          <h2 className="font-display font-black text-2xl sm:text-3xl text-center mt-2 max-w-2xl mx-auto">
            The platform validates the hardware play.
          </h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-3xl p-6 border border-pb-cream shadow-sm">
              <Wand2 className="h-5 w-5 text-pb-coral" />
              <p className="mt-3 font-display font-black text-lg">Platform (live)</p>
              <p className="mt-1 text-sm text-pb-soft leading-relaxed">
                PlateBuddies.com generates custom AI Buddies at $5 — proving willingness-to-pay and
                collecting first-party demand data for the physical kit.
              </p>
            </div>
            <div className="bg-white rounded-3xl p-6 border border-pb-cream shadow-sm">
              <Utensils className="h-5 w-5 text-pb-coral" />
              <p className="mt-3 font-display font-black text-lg">Starter Kit (in-development)</p>
              <p className="mt-1 text-sm text-pb-soft leading-relaxed">
                LED-ring plate + 6 collectible Buddy characters. Higher AOV + category entry into the
                $931M US children's tableware market.
              </p>
            </div>
            <div className="bg-white rounded-3xl p-6 border border-pb-cream shadow-sm">
              <Rocket className="h-5 w-5 text-pb-coral" />
              <p className="mt-3 font-display font-black text-lg">Comp proof</p>
              <p className="mt-1 text-sm text-pb-soft leading-relaxed">
                Tonies hit ~$681M revenue in 2025 with the same loop — collectible character + sensory
                device + repeat purchases. The model scales.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section data-testid="investors-contact" className="mt-16 text-center">
          <h2 className="font-display font-black text-2xl sm:text-3xl">Request the full financial model.</h2>
          <p className="mt-3 text-base text-pb-soft max-w-xl mx-auto">
            Execution plan + unit economics + go-to-market available on request. Open to strategic angels,
            family-brand operators, and retail buyers.
          </p>
          <a
            href="mailto:invest@platebuddies.com"
            data-testid="investors-cta"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-pb-coral text-white font-black text-base px-8 py-4 shadow-[0_14px_32px_rgba(242,106,52,0.4)] hover:scale-105 transition"
          >
            <Mail className="h-4 w-4" />
            invest@platebuddies.com
          </a>
        </section>
      </main>
      <Footer />
    </div>
  );
}

// =====================================================================
// PAGE SHELL — Gate vs Deck
// =====================================================================

export default function Investors() {
  useSeo({
    title: "Plate Buddies — Investor Overview",
    description:
      "Plate Buddies turns the family dinner into play. Interactive mealtime plate with LED celebrations + collectible Buddy characters, backed by the live PlateBuddies.com custom-Buddy platform.",
    url: "https://platebuddies.com/investors",
    noIndex: true,
  });

  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(GATE_KEY)) setUnlocked(true);
    } catch { /* noop */ }
    window.scrollTo(0, 0);
  }, []);

  const handleUnlock = (token) => {
    try {
      sessionStorage.setItem(GATE_KEY, token);
    } catch { /* noop */ }
    setUnlocked(true);
    window.scrollTo(0, 0);
  };

  const handleLock = () => {
    try {
      sessionStorage.removeItem(GATE_KEY);
    } catch { /* noop */ }
    setUnlocked(false);
    window.scrollTo(0, 0);
  };

  return unlocked ? <DeckContent onLock={handleLock} /> : <AccessGate onUnlock={handleUnlock} />;
}

/*
 * SOURCES — every number on the deck page is pulled verbatim from the published
 * PlateBuddies investor deck (currently hosted at makeahappyplate.com,
 * consolidated onto this page under platebuddies.com/investors to unify brand).
 *
 *  • "~50% of children 2–5 selective eating phase"  →  American Academy of Pediatrics
 *  • "$2B+ feeding solutions / year"            →  US feeding & nutrition category reports
 *  • "$931M US children's tableware market"     →  3rd-party market reports
 *  • "14.9M US children aged 2–5"               →  US Census Bureau population estimates
 *  • "~$681M Tonies 2025 revenue (USD equiv)"   →  Tonies SE reported 2025 revenue (€630M)
 *  • "6 collectible Buddy characters included"  →  Starter Kit spec, in-dev
 *  • "LED ring + press button to celebrate"     →  Starter Kit spec, in-dev
 *
 *  Deck available with full sourcing on request → invest@platebuddies.com
 */
